import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type SortChangedEvent,
} from 'ag-grid-community';
import { trendzGridTheme } from '@components/grid/trendzGridTheme';
import '@components/grid/agGridTheme.css';
import { buildForecastsColumns } from '../columns';
import {
  FORECAST_SORT_KEYS,
  type Forecast,
  type ForecastSortKey,
  type SortDirection,
} from '../schema';
import type { CommonResponse } from '@/types/api';

ModuleRegistry.registerModules([AllCommunityModule]);

interface Props {
  data: CommonResponse<Forecast> | undefined;
  onEdit: (row: Forecast) => void;
  onChart: (row: Forecast) => void;
  onSortChange: (sort: { sortBy?: ForecastSortKey; sortDir?: SortDirection }) => void;
}

const DEFAULT_COL_DEF = {
  resizable: true,
  sortable: false,
  filter: false,
  minWidth: 100,
  unSortIcon: true,
};

const SORT_KEY_SET = new Set<string>(FORECAST_SORT_KEYS);

function readSort(event: SortChangedEvent): {
  sortBy?: ForecastSortKey;
  sortDir?: SortDirection;
} {
  const state = event.api.getColumnState();
  const active = state.find(
    (c) => c.sort != null && c.colId && SORT_KEY_SET.has(c.colId),
  );
  if (!active || !active.sort || !active.colId) return {};
  return {
    sortBy: active.colId as ForecastSortKey,
    sortDir: active.sort as SortDirection,
  };
}

export function ForecastsGrid({ data, onEdit, onChart, onSortChange }: Props) {
  const columnDefs = useMemo(
    () => buildForecastsColumns({ onEdit, onChart }),
    [onEdit, onChart],
  );

  return (
    <div className="h-full w-full">
      <AgGridReact<Forecast>
        theme={trendzGridTheme}
        rowData={data?.rowData ?? []}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL_DEF}
        animateRows
        getRowId={(p) => p.data.id}
        suppressCellFocus
        suppressMultiSort
        rowHeight={56}
        onSortChanged={(e) => onSortChange(readSort(e))}
      />
    </div>
  );
}
